import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import { Plus, Camera, Wallet, ChevronRight, Sparkles } from "lucide-react";
import HelpButton from "../components/HelpButton";
import MerchantLogo from "../components/MerchantLogo";
import { CategoryPill } from "../lib/categoryIcons";
import { useAccounts } from "../lib/accounts";
import { useTour } from "../lib/tour";

// One-glance dashboard: one big number ("Cash left this week"), one primary
// action (Snap receipt), quick access to History. Everything else has been
// moved to /more. Forecast + full account balance are still one tap away.
export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [txns, setTxns] = useState([]);
  const { selectedId, accounts } = useAccounts();
  const tour = useTour();

  useEffect(() => {
    (async () => {
      const acc = selectedId ? `?account_id=${selectedId}` : "";
      const [s, t] = await Promise.all([
        api.get(`/summary${acc}`),
        api.get(`/transactions${acc}`),
      ]);
      setSummary(s.data);
      setTxns(t.data.slice(0, 5));
      // Forecast is a paid module — swallow 402 so free users still see the rest.
      // Honour the horizon the user last picked on the Forecast page so the
      // Dashboard peek matches when they hop back.
      try {
        const params = new URLSearchParams();
        if (selectedId) params.set("account_id", selectedId);
        params.set(
          "horizon",
          localStorage.getItem("cb_forecast_horizon") || "week"
        );
        const f = await api.get(`/forecast?${params.toString()}`);
        setForecast(f.data);
      } catch (e) {
        if (e?.response?.status !== 402) throw e;
      }
    })();
  }, [selectedId]);

  // Cash-left math: current balance − bills coming up (next 7d) + income coming in.
  const upcoming7d = forecast?.upcoming || [];
  const outflows7d = upcoming7d.filter((e) => e.delta < 0).reduce((s, e) => s + Math.abs(e.delta), 0);
  const income7d = upcoming7d.filter((e) => e.delta > 0).reduce((s, e) => s + e.delta, 0);
  const registerBalance = summary?.register_balance ?? 0;
  const hasForecast = !!forecast;
  const cashLeft = hasForecast ? registerBalance - outflows7d + income7d : registerBalance;

  const tone = cashLeft < 0 ? "danger" : cashLeft < 20 ? "warn" : "ok";
  const bg = tone === "danger" ? "#f4c2c2" : tone === "warn" ? "#fdecc8" : "#b5e4ca";
  const toneLabel =
    tone === "danger" ? "Careful — you're upside down" : tone === "warn" ? "Tight this week" : "Guilt-free zone";

  return (
    <div className="space-y-4" data-testid="dashboard">
      <div className="flex justify-end -mb-1">
        <HelpButton id="dashboard" />
      </div>

      {/* EMPTY-STATE COACH — shown when the user has never logged anything
          (no txns AND $0 balance across all their accounts). Hidden while the
          tour is actively running so we don't stack two "get started" prompts
          on top of each other. A friendly nudge that turns lostness into a
          one-tap action. */}
      {!tour?.running && txns.length === 0 && registerBalance === 0 && (
        <div
          data-testid="empty-state-coach"
          className="nb-card-lg p-5"
          style={{ background: "#fdecc8", boxShadow: "6px 6px 0 0 #1a1a1a" }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={14} strokeWidth={3} className="text-[#7b6ad0]" />
            <div className="label !mb-0">New around here?</div>
          </div>
          <div className="font-display font-black text-xl leading-tight mb-1">
            Nothing to see yet — let's fix that
          </div>
          <div className="text-sm font-medium text-[#4a4a4a] leading-snug mb-3">
            {accounts.length === 0
              ? "Give me 30 seconds and I'll walk you through adding your money and logging your first spend."
              : "Snap your first receipt or type in a spend — either way, your dashboard fills in instantly."}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="coach-start-tour"
              onClick={() => tour?.start?.()}
              className="nb-btn nb-btn-dark !py-3 flex-col !gap-1 !text-sm"
            >
              <Sparkles size={16} strokeWidth={3} />
              <span>Show me around</span>
            </button>
            <Link
              to="/snap"
              data-testid="coach-snap-shortcut"
              className="nb-btn nb-btn-positive !py-3 flex-col !gap-1 !text-sm"
            >
              <Camera size={16} strokeWidth={3} />
              <span>Snap a receipt</span>
            </Link>
          </div>
        </div>
      )}

      {/* HERO — one big spend-safe number. Auto-falls back to the register
          balance for users without any Bills/Paychecks set up yet. */}
      <div
        className="nb-card-lg p-6"
        data-testid="cash-hero-card"
        style={{ background: bg }}
      >
        <div className="label !mb-1 flex items-center gap-1.5">
          <Wallet size={12} strokeWidth={3} />
          {hasForecast ? "Safe to spend this week" : "You've got"}
        </div>
        <div
          data-testid="cash-hero-amount"
          className={`font-display font-black text-5xl sm:text-6xl tracking-tight ${cashLeft < 0 ? "balance-neg" : ""}`}
        >
          {fmtMoney(cashLeft)}
        </div>
        <div className="mt-2 text-xs font-bold text-[#1a1a1a]/80">
          {toneLabel}
          {hasForecast && upcoming7d.length > 0 && (
            <>
              {" "}· after {fmtMoney(outflows7d)} in bills
              {income7d > 0 ? ` + ${fmtMoney(income7d)} coming in` : ""}
            </>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 text-[11px] font-bold text-[#1a1a1a]/70">
          <span>Actual balance:</span>
          <span data-testid="register-balance" className="text-[#1a1a1a] font-black tabular-nums">
            {fmtMoney(registerBalance)}
          </span>
          {summary?.cleared_balance !== undefined && summary.cleared_balance !== registerBalance && (
            <>
              <span>·</span>
              <span>Confirmed:</span>
              <span data-testid="cleared-balance" className="text-[#1a1a1a] font-black tabular-nums">
                {fmtMoney(summary.cleared_balance)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* PRIMARY ACTION — Snap receipt is the fastest way to log spending, so
          it gets the biggest button. Manual entry is secondary. */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/snap"
          data-testid="snap-receipt-btn"
          className="nb-btn nb-btn-positive !py-4 flex-col !gap-1"
        >
          <Camera size={22} strokeWidth={3} />
          <span>Snap receipt</span>
        </Link>
        <Link
          to="/add"
          data-testid="add-transaction-btn"
          className="nb-btn !py-4 flex-col !gap-1"
        >
          <Plus size={22} strokeWidth={3} />
          <span>Type it in</span>
        </Link>
      </div>

      {/* RECENT — quick glance at last 5 with a full-history link. */}
      <div className="nb-card p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="label !mb-0">Recent</div>
          <Link to="/register" className="nb-tag hover:bg-[#d9d4f1]" data-testid="see-all-link">
            See all history →
          </Link>
        </div>
        {txns.length === 0 && (
          <div className="text-sm font-medium text-[#4a4a4a] py-6 text-center">
            Nothing logged yet. Tap <b>Snap receipt</b> above to add your first one.
          </div>
        )}
        <div className="divide-y-2 divide-[#1a1a1a]/10">
          {txns.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                {t.type !== "deposit" && <MerchantLogo payee={t.payee} size={30} />}
                <div className="min-w-0">
                  <div className="font-bold truncate">{t.payee}</div>
                  <div className="text-xs text-[#4a4a4a] font-medium flex items-center gap-2 flex-wrap">
                    <span>{shortDate(t.date)}</span>
                    {t.category && <CategoryPill category={t.category} size="xs" />}
                  </div>
                </div>
              </div>
              <div
                className={`font-display font-black text-lg ${
                  t.type === "deposit" ? "balance-pos" : ""
                }`}
              >
                {t.type === "deposit" ? "+" : "-"}
                {fmtMoney(t.amount)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FORECAST peek — small, dismissible-feeling chip that links to the full
          view. Keeps the promise of "at-a-glance" without hogging screen real
          estate on the Dashboard. */}
      {forecast && (
        <Link
          to="/forecast"
          data-testid="forecast-peek"
          className="nb-card p-4 flex items-center justify-between hover:bg-[#d9d4f1]/20 transition"
        >
          <div>
            <div className="label !mb-0.5">
              {forecast.horizon_mode === "week"
                ? "End of this week"
                : forecast.horizon_mode === "pay_period"
                ? "By next paycheck"
                : `${forecast.horizon_days || 14} days out`}
            </div>
            <div className="font-display font-black text-xl">
              {fmtMoney(forecast.projected_horizon ?? forecast.projected_90d ?? 0)}
            </div>
          </div>
          <ChevronRight size={20} strokeWidth={3} />
        </Link>
      )}

      {/* Everything else lives in the More app-drawer now — no more stacked
          quick-action rows here. */}
      <div className="pt-1">
        <Link
          to="/more"
          data-testid="more-link"
          className="w-full nb-btn nb-btn-secondary flex items-center justify-center gap-2"
        >
          Explore more features →
        </Link>
      </div>
    </div>
  );
}
